/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Tagging for outbound clicks that leave the site for a provider.
 *
 * Two things ride along on every provider link:
 *   - UTM params (utm_source / utm_medium / utm_content) so the provider's own
 *     analytics attributes the visit to us and to the surface it came from.
 *   - `ref=ere-vergelijk.nl` — the referral marker providers key their partner
 *     reporting off (…/signup?ref=ere-vergelijk.nl). It exists separately from
 *     the UTMs because signup funnels routinely strip utm_* between landing
 *     page and conversion, while `ref` is a value the provider persists itself.
 *
 * Isomorphic: `URL` exists in both Node and the browser, so this is safe on the
 * SSR/prerender path.
 */

export const OUTBOUND_REF = 'ere-vergelijk.nl';
export const OUTBOUND_UTM_SOURCE = 'ere-vergelijk.nl';

export interface OutboundTags {
  /** utm_medium — the surface the click came from (e.g. 'provider_profile'). */
  medium: string;
  /** utm_content — optional sub-surface (e.g. audience: 'zakelijk'). */
  content?: string;
}

/**
 * Decorate a provider URL with our UTM set + referral marker.
 *
 * UTM params are overwritten (we own that namespace), but an existing `ref` is
 * left alone: a signup URL configured with the provider's own affiliate/ref
 * value in the admin panel must keep it, or their partner reporting breaks.
 *
 * Relative or malformed URLs can't be parsed; they fall back to plain query
 * concatenation rather than being dropped.
 */
export function buildOutboundUrl(url: string, { medium, content }: OutboundTags): string {
  try {
    const u = new URL(url);
    u.searchParams.set('utm_source', OUTBOUND_UTM_SOURCE);
    u.searchParams.set('utm_medium', medium);
    if (content) u.searchParams.set('utm_content', content);
    if (!u.searchParams.has('ref')) u.searchParams.set('ref', OUTBOUND_REF);
    return u.toString();
  } catch {
    const params = [
      `utm_source=${OUTBOUND_UTM_SOURCE}`,
      `utm_medium=${encodeURIComponent(medium)}`,
      ...(content ? [`utm_content=${encodeURIComponent(content)}`] : []),
      ...(/[?&]ref=/.test(url) ? [] : [`ref=${OUTBOUND_REF}`]),
    ].join('&');
    return url + (url.includes('?') ? '&' : '?') + params;
  }
}
